// React
import React, { useEffect, useState, useRef } from 'react';

// Components
import Header from '@custom/Header/Header'
import Barcode from 'react-barcode'

// Assets
import { phoneIcon, mailIcon, mapIcon, printerIcon, mailIconwhite, mapIconWhite, phoneIconWhite, websiteIcon } from '../../ui/assets/svgIcons/icon'
import { useParams } from 'react-router-dom';

import './Preview.css'
import { Icon, Loader } from '@ohif/ui';
import { AppDispatch, RootState } from './../store/store';
import { useSelector, useDispatch } from 'react-redux';
import appointmentDetails from '@custom/rtk-apis/appointment/appointmentDetails';
import { calculateAge, capitalizeWords, formatDate, formatTime, getCurrencySymbol } from '@custom/Utils/utils';
import { useTheme } from "../../ui/src/contextProviders"
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';
import generatePdfApi from '@custom/rtk-apis/appointment/generatePDF';
import getPackage from '@custom/rtk-apis/billing/getBilling';
import getInvestigationApi from '@custom/rtk-apis/billing/getInvestigation';

// Ui
export default function PreviewAppointments() {
  const contentRef = useRef();
  const dispatch = useDispatch<AppDispatch>()
  const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);
  const packageData = useSelector((state: RootState) => state?.getPackageResponse);
  const investigationData = useSelector((state: RootState) => state?.getInvestigationResponse);
  let { appointmentId } = useParams();
  const printRef = useRef()
  const isDarkMode = useTheme();

  // States
  const [showMenu, setShowMenu] = useState(false)
  const [currency, setCurrency] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    dispatch(appointmentDetails({ id: appointmentId }))
    dispatch(getBillingGeneralSettings());
    dispatch(getPackage({}));
    dispatch(getInvestigationApi());
  }, [dispatch])

  useEffect(() => {
    if (billingGeneralSettingsData?.result?.currency) {
      setCurrency(billingGeneralSettingsData?.result?.currency);
    }
  }, [billingGeneralSettingsData]);

  const { msg, result, loading } = useSelector((state: RootState) => {
    return state?.appointmentDetailResponse;
  });

  const getServicesIncluded = (packageName: string, index: number) => {
    const packageMatch = packageData?.result?.data?.find((pkg: any) => pkg.packageName === packageName);
    if (packageMatch) {
      return packageMatch.servicesIncluded;
    }

    const investigationMatch = investigationData?.result?.data?.find((inv: any) => inv['Package Name'] === packageName);
    if (investigationMatch) {
      return investigationMatch['Services Included'].split(', ').map((service: string) => service.trim());
    }

    return [];
  };

  const downloadPDF = async () => {
    const downloadWindow = window.open('', '_blank');

    downloadWindow.document.write(`
      <html>
        <head>
          <title>Preparing PDF</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              text-align: center;
              padding-top: 100px;
            }
            .loader {
              border: 8px solid #f3f3f3;
              border-top: 8px solid #3498db;
              border-radius: 50%;
              width: 60px;
              height: 60px;
              animation: spin 1s linear infinite;
              margin: 20px auto;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <h3>Processing your request. PDF will open shortly...</h3>
          <div class="loader"></div>
        </body>
      </html>
    `);
    downloadWindow.document.close();

    try {
      setPdfLoading(true);

      const response = await dispatch(generatePdfApi(appointmentId)).unwrap();

      if (response?.filePath) {
        const filePath = response.filePath;
        downloadWindow.location.href = filePath;
      } else {
        downloadWindow.close();
        alert("Failed to retrieve the PDF. Please try again.");
      }
    } catch (error) {
      downloadWindow.close();
      alert("An error occurred while generating the PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  const calculateSubTotal = () => {
    let total = 0;
    if (result?.price) total += result.price;
    if (result?.extraModalities?.length > 0) {
      total += result.extraModalities.reduce((sum, extra) => sum + (extra.price || 0), 0);
    }
    if (result?.investigationPrice) {
      const prices = result.investigationPrice.split(',').map(price => parseFloat(price.trim()) || 0);
      total += prices.reduce((sum, price) => sum + price, 0);
    }
    return total.toFixed(2);
  };

  return (
    <div>
      <Header title={'Appointment Preview'} isBack={true} />

      <section className='bg-white flex bg items-center justify-between px-4 pt-4'>
        <p className={`text-[18px] font-semibold ${isDarkMode?.isDarkMode ? "text-white" : "text-black"}`}>Patient Appointment</p>

        <div className='flex flex-row items-center gap-2'>
          <button onClick={downloadPDF} className={`bg-green-500 ${isDarkMode?.isDarkMode ? "text-white" : "text-black"} px-2 py-1 rounded border border-black flex gap-2 items-center text-[14px] font-semibold`}>
            <img src={printerIcon} />
            Print
          </button>
        </div>
      </section>

      <div ref={contentRef} className='watermark-container' style={{ overflowX: 'auto' }} onClick={() => setShowMenu(false)}>
        <div className="bg-white px-4 content" style={{ minWidth: '1200px', color: '#49475A' }}>

          <section className="flex flex-row p-2">
            <div className="basic-1/3 p-4">
              <div className="h-full">
                <div className='flex flex-row h-full w-full'>
                  <div className="block mx-auto" style={{ width: '150px', maxHeight: '150px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 150px' }}>
                    {result?.hospitalDetail?.hospitalLogo ? (
                      <img src={result?.hospitalDetail?.hospitalLogo} style={{ height: '100%' }} alt="Hospital Logo" />
                    ) : (
                      <Icon name="DummyLogo"></Icon>
                    )}
                  </div>
                  <div className='border-l-2 border-[#0F8491] ml-4 h-full'></div>
                </div>
              </div>
            </div>
            <div className="basic-2/3 p-4 w-full ml-4">
              <div>
                <p className='text-[#0F8491] text-3xl  font-semibold'>{result?.hospitalDetail?.hospitalHeader?.hospitalName ? capitalizeWords(result?.hospitalDetail?.hospitalHeader?.hospitalName) : capitalizeWords(result?.hospitalDetail?.hospitalName)}</p>
                <ul className="">
                  {result?.hospitalDetail?.specialitites?.map((item, index) => (
                    <li className={`text-[14px] ${isDarkMode?.isDarkMode ? "text-white" : "text-[#49475A]"} relative pl-4`} key={index}>
                      <span className={`absolute left-0 top-1 ${isDarkMode?.isDarkMode ? "text-white" : "text-black"}`}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>

              </div>
              <div className='flex flex-row gap-4 border-t-2 border-[#0F8491] pt-2 mt-2'>
                <div className='flex flex-row gap-2'>
                  <img src={phoneIcon} alt="" />
                  <p className={`text-[14px] font-normal ${isDarkMode?.isDarkMode ? "text-white" : "color-[#535353]"}`}>{result?.hospitalDetail?.hospitalHeader?.contactNumberCountryCode ? result?.hospitalDetail?.contactNumberCountryCode : result?.hospitalDetail?.hospitalHeader?.contactNumberCountryCode} {result?.hospitalDetail?.hospitalHeader?.contactNumber ? result?.hospitalDetail?.hospitalHeader?.contactNumber : result?.hospitalDetail?.contactNumber}</p>
                </div>
                <div className='flex flex-row gap-2'>
                  <img src={mailIcon} alt="" />
                  <p className={`text-[14px] font-normal ${isDarkMode?.isDarkMode ? "text-white" : "color-[#535353]"}`}>{result?.hospitalDetail?.hospitalHeader?.email ? result?.hospitalDetail?.hospitalHeader?.email : result?.hospitalDetail?.hospitalEmailId}</p>
                </div>
                <div className='flex flex-row gap-2'>
                  <img src={mapIcon} alt="" />
                  <p className={`text-[14px] font-normal ${isDarkMode?.isDarkMode ? "text-white" : "color-[#535353]"}`}>{result?.hospitalDetail?.hospitalHeader?.address ? result?.hospitalDetail?.hospitalHeader?.address + ' ' + result?.hospitalDetail?.hospitalHeader?.state + ' ' + result?.hospitalDetail?.hospitalHeader?.city + ' ' + result?.hospitalDetail?.hospitalHeader?.zipCode : result?.hospitalDetail?.address + ' ' + result?.hospitalDetail?.state + ' ' + result?.hospitalDetail?.city + ' ' + result?.hospitalDetail?.zipCode}</p>
                </div>
                {result?.hospitalDetail?.hospitalHeader?.website || result?.hospitalDetail?.website && (
                  <div className='flex flex-row gap-2'>
                    <img src={websiteIcon} alt="" />
                    <p className={`text-[14px] font-normal ${isDarkMode?.isDarkMode ? "text-white" : "color-[#535353]"}`}>{result?.hospitalDetail?.hospitalHeader?.website ? result?.hospitalDetail?.hospitalHeader?.website : result?.hospitalDetail?.website}</p>
                  </div>
                )}
              </div>

            </div>
            <div className="basic-1/3 p-4">
              <p className={`text-[14px] w-full font-bold mb-1 ${isDarkMode?.isDarkMode ? "text-white" : "text-black"}`}><span className={`${isDarkMode?.isDarkMode ? "text-white" : "text-[#49475A]"}`}>UHID:</span> <span>{result?.patientInfo?.UHID || "---"}</span></p>
              <span>
                {result?.patientInfo?.UHID && <Barcode displayValue={false} width={1} height={40} value={result?.patientInfo?.UHID} />}
              </span>
            </div>
          </section>

          <section className='bg-[#0F8491] p-2'>
            <p className='text-[17.53px] font-semibold text-white text-center'>Appointment Receipt</p>
          </section>

          <section
            className='p-1 border border-black rounded-md mt-8'
          >
            <table className='patient-info-container'>
              <tbody style={{ borderRadius: '10px' }}>
                <tr className='p-2'>
                  <td className='info-label'>Name :</td>
                  <td className='info-data'>
                    {result?.patientInfo?.firstName ? `${capitalizeWords(result?.patientInfo?.firstName)} ${capitalizeWords(result?.patientInfo?.lastName)}` : '--'}
                  </td>
                  <td className='info-label'>Date :</td>
                  <td className='info-data'>{result?.date ? formatDate(result?.date) : '--'}</td>
                  <td className='info-label'>Patient ID :</td>
                  <td className='info-data'>{result?.patientInfo?.patientId ? result?.patientInfo?.patientId : '--'}</td>
                </tr>
                <tr>
                  <td className='info-label'>Age :</td>
                  <td className='info-data'>{result?.patientInfo?.dob ? calculateAge(result?.patientInfo?.dob) : '--'}</td>
                  <td className='info-label'>Time :</td>
                  <td className='info-data'>{result?.time ? formatTime(result?.time) : '--'}</td>
                  <td className='info-label'>Referring Doctor :</td>
                  <td className='info-data'>{result?.referDoctor ? result?.referDoctor : '--'}</td>
                </tr>
                <tr>
                  <td className='info-label'>Gender :</td>
                  <td className='info-data'>{result?.patientInfo?.gender ? result?.patientInfo?.gender : '--'}</td>
                  <td className='info-label'>Modality Type :</td>
                  <td className='info-data'>{result?.modality ? result?.modality : '--'}</td>
                  <td className='info-label'>Referring Doctor Contact :</td>
                  <td className='info-data'>{result?.referDoctorContact ? `${result?.referDoctorDialCode || ""} ${result?.referDoctorContact}` : "--"}</td>
                </tr>
                <tr>
                  <td className='info-label'>Phone Number :</td>
                  <td className='info-data'>{result?.patientInfo?.contactInfo?.dialCode + " " + result?.patientInfo?.contactInfo?.mobileNo}</td>

                  <td className='info-label'>Study Region :</td>
                  <td className='info-data'>{result?.studyRegion ? result?.studyRegion : '--'}</td>
                  <td className='info-label'>Referring Doctor Specialty :</td>
                  <td className='info-data'>{result?.referDoctorSpec ? capitalizeWords(result?.referDoctorSpec) : '--'}</td>
                </tr>
                <tr>
                  <td className='info-label'>Consulting Doctor :</td>
                  <td className='info-data'>{result?.doctorName ? result?.doctorName : '--'}</td>
                  <td className='info-label'>Status :</td>
                  <td className='info-data'>{result?.status ? result?.status : '--'}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="mt-4">
            <table className={`w-full border-collapse ${isDarkMode?.isDarkMode ? 'border-white' : 'border-black'}`}>
              <thead>
                <tr className="bg-gray-200 text-black">
                  <th className="border-l border-t border-r border-b border-black p-2 text-left">Description</th>
                  <th className="border-t border-r border-b border-black p-2 text-right" style={{ width: '8%' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {result?.modality && (
                  <tr
                    className={
                      (!result?.extraModalities?.length && !result?.serviceInvestigation?.length)
                        ? `${isDarkMode?.isDarkMode ? 'border-b border-white' : 'border-b border-black'}`
                        : ''
                    }
                  >
                    <td className={`border-l border-r ${isDarkMode?.isDarkMode ? 'border-white text-white' : 'border-black'} p-2 text-[14px]`}>
                      <span>
                        <strong>{capitalizeWords(result.modality)}</strong>
                        {result?.studyRegion && ` - ${result.studyRegion}`}
                        {result?.machineName && ` (${result.machineName})`}
                      </span>
                    </td>
                    <td className={`border-r ${isDarkMode?.isDarkMode ? 'border-white text-white' : 'border-black'} p-2 text-right text-[14px]`} style={{ width: '8%' }}>
                      {`${getCurrencySymbol(currency)}${result.price}`}
                    </td>
                  </tr>
                )}
                {result?.extraModalities?.length > 0 &&
                  result.extraModalities.map((extra, index) => {
                    const isLast = index === result.extraModalities.length - 1;
                    const addBottomBorder = isLast && (!result?.serviceInvestigation || result.serviceInvestigation.length === 0);

                    return (
                      <tr key={`extra-${index}`} className={addBottomBorder ? `${isDarkMode?.isDarkMode ? 'border-b border-white' : 'border-b border-black'}` : ''}>
                        <td className={`border-l border-t border-r ${isDarkMode?.isDarkMode ? 'border-white text-white' : 'border-black'} p-2 text-[14px]`}>
                          <span>
                            <strong>{capitalizeWords(extra.modality)}</strong>
                            {extra.studyRegion && ` - ${extra.studyRegion}`}
                            {extra.machineName && ` (${extra.machineName})`}
                          </span>
                        </td>
                        <td className={`border-r border-t ${isDarkMode?.isDarkMode ? 'border-white text-white' : 'border-black'} p-2 text-right text-[14px]`} style={{ width: '8%' }}>
                          {`${getCurrencySymbol(currency)}${extra.price}`}
                        </td>
                      </tr>
                    );
                  })
                }

                {result?.serviceInvestigation?.length > 0 && (
                  <>
                    {result.serviceInvestigation.map((investigation, index) => {
                      const prices = result?.investigationPrice
                        ? result.investigationPrice.split(',').map((price) => price.trim())
                        : [];
                      const price = prices[index] || '--';
                      const investigationServices = getServicesIncluded(investigation, index);

                      return (
                        <React.Fragment key={`investigation-${index}`}>
                          <tr>
                            <td className={`border-l border-t border-r ${isDarkMode?.isDarkMode ? 'border-white text-white' : 'border-black'} p-2 text-[14px] font-medium`}>
                              <span>
                                <strong>{capitalizeWords(investigation)}</strong>
                              </span>
                            </td>
                            <td className={`border-t border-r ${isDarkMode?.isDarkMode ? 'border-white text-white' : 'border-black'} p-2 text-right text-[14px]`} style={{ width: '8%' }}>
                              {`${getCurrencySymbol(currency)}${price}`}
                            </td>
                          </tr>
                          {investigationServices.length > 0 &&
                            investigationServices.map((service, serviceIndex) => {
                              const isLast = serviceIndex === investigationServices.length - 1;
                              return (
                                <tr
                                  key={`service-${investigation}-${serviceIndex}`}
                                  className={isLast ? `${isDarkMode?.isDarkMode ? 'border-b border-white' : 'border-b border-black'}` : ''}
                                >
                                  <td className={`border-l border-r ${isDarkMode?.isDarkMode ? 'border-white text-white' : 'border-black'} p-1 text-[14px] pl-6`}>
                                    - {capitalizeWords(service)}
                                  </td>
                                  <td className={`border-r ${isDarkMode?.isDarkMode ? 'border-white' : 'border-black'} p-1 text-right text-[14px]`} style={{ width: '8%' }}></td>
                                </tr>
                              );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
          </section>

          <section className='mb-4'>
            <div className='border-t-2 border-[#000] pt-4 mt-2 mb-4 mt-4' style={isDarkMode?.isDarkMode ? { marginTop: '100px', color: '#ffffff' } : { marginTop: '100px', color: '#49475A' }}>
              <p><strong>Note:</strong> Patient should be present in the premises in the given date and time with this appointment letter to visit Consulting Doctor.</p>
            </div>
          </section>

          <div className='bg-[#0F8491] pt-2 pb-2 pr-2 pl-2'>
            <div className='flex flex-wrap gap-6 justify-between items-center border-t-2 border-[#0F8491] pt-2 mt-2 text-white'>
              {(result?.hospitalDetail?.hospitalFooter?.contactNumber || result?.hospitalDetail?.contactNumber) && (
                <div className='flex flex-row gap-2 min-w-[200px]'>
                  <img src={phoneIconWhite} alt="" />
                  <p className='text-[14px] font-normal text-white'>
                    {(result?.hospitalDetail?.hospitalFooter?.contactNumberCountryCode || result?.hospitalDetail?.contactNumberCountryCode) + ' '}
                    {(result?.hospitalDetail?.hospitalFooter?.contactNumber || result?.hospitalDetail?.contactNumber)}
                  </p>
                </div>
              )}
              {(result?.hospitalDetail?.hospitalFooter?.email || result?.hospitalDetail?.hospitalEmailId) && (
                <div className='flex flex-row gap-2 min-w-[200px]'>
                  <img src={mailIconwhite} alt="" />
                  <p className='text-[14px] font-normal text-white'>
                    {result?.hospitalDetail?.hospitalFooter?.email || result?.hospitalDetail?.hospitalEmailId}
                  </p>
                </div>
              )}
              <div className='flex flex-row gap-2 min-w-[250px]'>
                <img src={mapIconWhite} alt="" />
                <p className='text-[14px] font-normal text-white'>
                  {(result?.hospitalDetail?.hospitalHeader?.address || result?.hospitalDetail?.address) + ' '}
                  {(result?.hospitalDetail?.hospitalHeader?.state || result?.hospitalDetail?.state) + ' '}
                  {(result?.hospitalDetail?.hospitalHeader?.city || result?.hospitalDetail?.city) + ' '}
                  {(result?.hospitalDetail?.hospitalHeader?.zipCode || result?.hospitalDetail?.zipCode)}
                </p>
              </div>
            </div>
          </div>

        </div>
        <div className="watermark-text">
          <Icon
            name="appointmentPreview"
            className="w-[100%] h-[100%]"
            alt="appointmentPreview"
          />
        </div>
      </div >
      <Loader isLoaderOpen={loading || pdfLoading}></Loader>
    </div >
  )
}
